import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import RadarBurstDetail, { BURST_COLORS } from "./RadarBurstDetail";
import RadarTrends, { SERIES } from "./RadarTrends";
import RadarWaterfall from "./RadarWaterfall";
import SoilSurfaceControl from "./SoilSurfaceControl";
import { useSoilSurface } from "hooks/useSoilSurface";
import { soilLine } from "utils/soilSurface";
import { Age, Fact, More, Segment, sentence } from "./radarUi";
import { useRadarBursts, useRadarCaptureDetails } from "hooks/useRadarData";
import { duration, fieldTime } from "utils/fieldFormat";
import {
  formatMm as mm,
  newestWith,
  onCellular,
  ordinal,
  sgtTime as sgt,
  timeOf,
} from "utils/radarDiagnostics";
import { STATES, energyText, readAnalysis } from "utils/radarAnalysis";
import { keepNoteText, readEdge } from "utils/radarEdge";
import { ECHO_SNR_DB, clipping } from "utils/radarSignal";
import {
  RAW_MODE_DEFAULT,
  hasSpectrum,
  rawModeText,
  readRawAlt,
  readRawMode,
  windowText,
} from "utils/radarStream";
import {
  BURST_DEFAULTS,
  BURST_WINDOWS,
  DAY_MS,
  analysisPending,
  mergeBursts,
  burstAccount,
  burstClipping,
  burstEstimate,
  burstSummary,
  burstText,
  burstTrend,
  burstWaterfall,
  cadenceText,
  expectedBursts,
  liveSpans,
  readBurst,
  skipText,
  sparkline,
  uploadCadence,
} from "utils/radarBurst";
import {
  batchState,
  capturesPerWake,
  clockText,
  countersNote,
  countersText,
  dayClockText,
  expectedRich,
  hourParts,
  listEntries,
  readRich,
  readingCounts,
  readingCountsText,
  richCounters,
  richOn,
  richSettingsText,
  richStart,
  richTally,
  rtctBuild,
  staleRichCounts,
  uploadExtras,
  wakeCadence,
  wakeEveryText,
  wakeText,
} from "utils/radarRich";

// Rich readings and bursts (Radar analysis). From firmware .58 a node takes a
// raw radar capture on every normal wake (rich readings: the beat signal, the
// spectrum or both), kept on the node and sent with its uploads as one small
// burst a wake ("kind" rich); from firmware .56 it can also take a short
// burst of full captures on a wake that uploads (classic bursts). Both come
// without live mode, on battery or cellular too. The list newest first (the
// rich readings of each hour as one row that opens on its wakes), one in full
// (with our analysis's labels and, from firmware .57, the energy it took) and
// the history over 24 h, 7 or 30 days. Both are set in Radar rich data on Node
// Control. The page may hold the window (`minutes`, `onMinutes`, shared with
// its timeline), hear of the newest burst (`onNewest`) and ask for one to be
// opened (`focus`: {id, n}). From firmware .62 a node can hold a rich wake's
// raw captures back (its raw uploads): each sent rich reading says why it was
// sent (its reading's keep note), and the wakes held back - from the page's
// analysis series (`held`: its points with heldBack, every one of the window
// unless the series is thinned out, `heldComplete` false) - sit in their hour
// with our decision from the node's echo list, with nothing to open.
// The clipping trend reads the stored records of this many beat signals.
const CLIP_DETAILS = 24;
const LIST_PAGE = 10;
const HISTORY_LABELS = {
  aria: "Burst history: choose a burst to inspect",
  empty: (span) =>
    `No bursts in the last ${span}. Choose a longer window; a node bursts on its uploading wakes once bursts are on.`,
  hint: "Point at the history to read it; click a column to inspect that burst.",
  pick: "Radar's median pick",
  ours: "Our estimate",
  gap: "No burst",
  caption: (span) =>
    `One column per burst over the last ${span}: its mean spectrum, distance from the radar down, colour from weak (dark) to strong (yellow) between the 20th percentile and the strongest bins, in 0.5 dB steps. A burst of the beat signal alone (no radar spectrum) shows our FFT of it, computed the radar's way (no window, its scale), so every wake has a column; rich readings give a column a wake.`,
};
const BURST_SERIES = [
  ...SERIES.map((s) =>
    s.key === "echoes"
      ? {
          ...s,
          title: "Echoes over the floor",
          hint: `Ours, on each burst's mean: ${ECHO_SNR_DB} dB over the floor. More echoes: clutter, insects or drops in the beam.`,
        }
      : s.key === "pick"
        ? { ...s, title: "Radar's median pick" }
        : s,
  ),
  {
    key: "flicker",
    title: "Flicker (strongest)",
    unit: "dB",
    digits: 1,
    hint: "The largest spread between a burst's captures beyond the near field: insects, drops or ripple. About 1-2 dB when steady on node 1. It needs two spectra in a burst, so rich readings (one spectrum a wake) have none.",
    color: BURST_COLORS.flicker,
  },
];
const CLIP_SERIES = {
  key: "clip",
  title: "Clipping (beat signals)",
  unit: "%",
  digits: 1,
  hint: "Share of each burst's beat-signal samples clipped at the receiver's rails (it saturates on a strong close echo; node 1 reads about 6%), as the server measures it.",
  color: "#b07a10",
};
const countText = (n) =>
  n >= 10
    ? Math.round(n).toLocaleString()
    : n.toLocaleString(undefined, { maximumFractionDigits: 1 });
const plural = (n, word) =>
  `${countText(n)} ${word}${Math.round(n * 10) === 10 ? "" : "s"}`;
const mmRange = (lo, hi) =>
  Math.round(lo) === Math.round(hi)
    ? mm(lo)
    : `${Math.round(lo).toLocaleString()}–${mm(hi)}`;
// The bursts to expect beside those seen (expectedBursts()): outside live
// mode, saying so, when the node's readings show it ran.
const expectedText = (e) =>
  !e.live
    ? ` · about ${countText(e.count)} expected`
    : ` · ${e.count < 0.05 ? "none expected" : `about ${countText(e.count)} expected`} outside live mode · bursts pause while live mode runs (${duration(e.liveMs / 1000)} live${e.liveNow ? ", running now" : ""})`;
// The rich captures to expect beside those seen (expectedRich()): normal
// wakes outside live mode × the captures each takes (+ the extra ones of the
// wakes that upload); the wakes since the newest reading are still on the
// node, for its next upload, and counted apart.
const richExpectedText = (e, uploadSeconds) => {
  // Whole wakes, readings and uploads, so the total is what the sum says.
  const wakes = Math.round(e.wakes);
  const readings = Math.round(e.wakes / (e.every || 1));
  const uploads = Math.round(e.uploads);
  const count = readings * e.perWake + (e.extras ? uploads * e.extras : 0);
  // radar_rich on every Nth wake (firmware .59): the readings of the wakes.
  const how = `${
    e.every > 1
      ? `${plural(readings, "reading")} × ${e.perWake}, on every ${ordinal(e.every)} of ${plural(wakes, "wake")}`
      : `${plural(wakes, "wake")} × ${e.perWake}`
  }${e.extras ? ` + ${plural(uploads, "upload")} × ${e.extras}` : ""}`;
  // Wakes since the newest reading that hold captures still on the node.
  const waiting = e.waiting?.captures
    ? e.waiting.wakes === 1
      ? ` · the wake after ${clockText(e.waiting.since)} (${plural(e.waiting.captures, "capture")}) is still on the node, for its next upload`
      : ` · the ${countText(e.waiting.wakes)} wakes after ${clockText(e.waiting.since)} (about ${plural(e.waiting.captures, "capture")}) are still on the node, for its next upload`
    : "";
  return [
    ` · ${count === 0 ? (e.waiting ? "none due yet" : "none expected") : `about ${countText(count)} expected${e.waiting ? " so far" : ""} (${how})`}${e.live ? " outside live mode" : ""}`,
    e.live
      ? ` · rich readings pause while live mode runs (${duration(e.liveMs / 1000)} live${e.liveNow ? ", running now" : ""})`
      : "",
    waiting,
    waiting && uploadSeconds ? ` (every ${duration(uploadSeconds)})` : "",
  ].join("");
};
// The newest rich reading's age in words: rich readings come with the node's
// uploads, so up to one upload interval old is normal, not stale.
const batchText = (state, uploadSeconds) =>
  state === "fresh"
    ? ` · up to ${duration(uploadSeconds)} old is normal (it comes with the node's uploads)`
    : state === "overdue"
      ? ` · overdue: the node uploads every ${duration(uploadSeconds)}, so newer ones should have arrived`
      : "";
// What a burst's captures hold: "beat + spectrum", "beat signal",
// "spectrum"; in full for its description.
const partsOf = (s, long = false) => {
  const beat = s.captures.some((c) => c?.has_adc);
  const spectrum = s.captures.some(hasSpectrum);
  return beat && spectrum
    ? long
      ? "beat signal and spectrum"
      : "beat + spectrum"
    : beat
      ? "beat signal"
      : spectrum
        ? "spectrum"
        : "no radar data";
};

export default function RadarBurstPanel({
  mac,
  settings = null,
  samples = [],
  refresh = 0,
  minutes = null,
  onMinutes,
  onNewest,
  focus = null,
  held = null,
  heldComplete = true,
}) {
  const reported = useMemo(
    () => settings?.reported || {},
    [settings?.reported],
  );
  const rules = settings?.rules || {};
  const rb = readBurst(reported, rules);
  const rr = readRich(reported, rules);
  // The raw-upload rules the node runs (firmware .62), for its keep notes.
  const keepRules = readEdge(reported, rules);
  const allowed = Array.isArray(settings?.allowed_keys)
    ? settings.allowed_keys
    : null;
  const modeAllowed = !!allowed?.includes("radar_raw_mode");
  const reportedMode = readRawMode(reported.radar_raw_mode);
  const reportedAlt = readRawAlt(reported.radar_raw_alt);

  const [range, setRange] = useState(() => {
    const m = minutes ?? 1440;
    return { minutes: m, from: Date.now() - m * 60000 };
  });
  const [selectedId, setSelectedId] = useState(null);
  const [shownCount, setShownCount] = useState(LIST_PAGE);
  const [spectrumRange, setSpectrumRange] = useState("near");
  // The hours of rich readings opened in the list.
  const [open, setOpen] = useState(() => new Set());
  const detailCache = useRef(new Map());
  const clipCache = useRef(new Map());
  const summaryCache = useRef(new WeakMap());
  const detailRef = useRef(null);
  // A window the page holds: a longer one loads older bursts too.
  useEffect(() => {
    if (minutes === null || minutes === undefined) return;
    setRange((old) =>
      old.minutes === minutes
        ? old
        : {
            minutes,
            from: Math.min(old.from, Date.now() - minutes * 60000),
          },
    );
  }, [minutes]);

  // ---- Data ----
  // Rich readings (one a wake) and the classic bursts load apart (?kind=),
  // so a dense node's rich readings never crowd the classic bursts out of a
  // long window; the list and the history show them together.
  const richLoad = useRadarBursts(mac, {
    from: range.from,
    refresh,
    kind: "rich",
  });
  const classicLoad = useRadarBursts(mac, {
    from: range.from,
    refresh,
    kind: "burst",
  });
  const merged = useMemo(
    () => mergeBursts(classicLoad.bursts, richLoad.bursts),
    [classicLoad.bursts, richLoad.bursts],
  );
  const loads = [richLoad, classicLoad];
  const bursts = {
    bursts: merged,
    loaded: loads.every((l) => l.loaded),
    error: richLoad.error || classicLoad.error,
    offset: (richLoad.loaded ? richLoad : classicLoad).offset,
    unit: richLoad.unit ?? classicLoad.unit,
    // Complete from here (the classic bursts' start when the rich readings
    // stop short, as the notice says).
    covered: loads.every((l) => l.covered !== null)
      ? Math.min(richLoad.covered, classicLoad.covered)
      : null,
  };
  const summaries = useMemo(
    () =>
      bursts.bursts.map((b) => {
        let s = summaryCache.current.get(b);
        if (!s) {
          s = burstSummary(b);
          summaryCache.current.set(b, s);
        }
        return s;
      }),
    [bursts.bursts],
  );
  const byId = useMemo(
    () => new Map(bursts.bursts.map((b) => [b.id, b])),
    [bursts.bursts],
  );
  const nowMs = Date.now() + bursts.offset;
  const newest = summaries[0] || null;
  const selected =
    (selectedId !== null && summaries.find((s) => s.id === selectedId)) ||
    newest;
  const selectedBurst = selected ? byId.get(selected.id) || null : null;
  const details = useRadarCaptureDetails(
    selectedBurst ? (selectedBurst.captures || []).map((c) => c?.id) : [],
    detailCache,
  );
  const missing = bursts.error?.status === 404;
  // The page hears of the newest burst (its summary shows its analysis).
  const newestBurst = newest ? byId.get(newest.id) || null : null;
  const onNewestRef = useRef(onNewest);
  onNewestRef.current = onNewest;
  useEffect(() => {
    if (bursts.loaded) onNewestRef.current?.(newestBurst);
  }, [newestBurst, bursts.loaded]);
  // A burst the page asks for (a point on its timeline) opens below.
  useEffect(() => {
    if (!focus || focus.id === null || focus.id === undefined) return;
    if (!summaries.some((s) => String(s.id) === String(focus.id))) return;
    const id = summaries.find((s) => String(s.id) === String(focus.id)).id;
    setSelectedId(id === summaries[0]?.id ? null : id);
    detailRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    // Only a new request opens a burst; new bursts arriving do not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus]);

  // ---- Rich readings beside classic bursts ----
  const rich = useMemo(
    () => summaries.filter((s) => s.kind === "rich"),
    [summaries],
  );
  const classic = useMemo(
    () => summaries.filter((s) => s.kind !== "rich"),
    [summaries],
  );
  // Rich readings show once the node reports the setting or one arrives;
  // their cadence and counts while they run or have arrived.
  const richShown = rr.mask !== null || rich.length > 0;
  const richActive = richOn(rr) || rich.length > 0;
  const newestRich = rich[0] || null;
  // The burst the "Last burst" line speaks of: the newest classic one once
  // rich readings are about.
  const lastBurst = richShown ? classic[0] || null : newest;

  // ---- The node: telemetry and cadence ----
  const newestSample = useMemo(
    () =>
      samples.reduce(
        (best, x) =>
          (timeOf(x?.timestamp) ?? -Infinity) >
          (timeOf(best?.timestamp) ?? -Infinity)
            ? x
            : best,
        null,
      ),
    [samples],
  );
  const cellular = onCellular(newestSample?.raw);
  const cadence = useMemo(
    () => uploadCadence(reported, samples, { cellular }),
    [reported, samples, cellular],
  );
  const wakes = useMemo(
    () => wakeCadence(reported, samples, { cellular }),
    [reported, samples, cellular],
  );
  // The node's counters: the settings endpoint's `counters`, else its
  // read-back, else a reading.
  const nodeCounters = settings?.counters;
  const counters = useMemo(
    () => richCounters(nodeCounters, reported, samples),
    [nodeCounters, reported, samples],
  );
  // Firmware .58-.59 send their counters again only with a settings change
  // (staleRichCounts()): there captured and failed come from the readings of
  // the last 24 h, the queue's from the node; from .60 the node's own four.
  // Each with the time the node took them (counters_at).
  const fw = newestWith(samples, "fw")?.value;
  const build = rtctBuild(fw);
  const uncounted = staleRichCounts(fw);
  const countersAt = settings?.counters_at ?? null;
  const fromReadings =
    uncounted === null ? null : readingCounts(samples, nowMs - DAY_MS);
  const shownCounters =
    uncounted !== null && counters
      ? { ...counters, ok: null, fail: null }
      : counters;
  const countersWords = countersText(shownCounters);
  // The wake's own account of its newest burst.
  const lastAccount = useMemo(
    () => newestWith(samples, "jjy_burst"),
    [samples],
  );
  const account = burstAccount(lastAccount?.value);
  const skipped =
    account?.skip && lastAccount.at > (lastBurst?.ms ?? -Infinity) + 60000
      ? { t: lastAccount.t, text: skipText(account.skip) }
      : null;
  const running =
    rb.mask > 0
      ? burstEstimate({
          mask: rb.mask,
          mode: reportedMode ?? RAW_MODE_DEFAULT,
          alt: reportedAlt === 1,
          n: rb.n ?? BURST_DEFAULTS.n,
          gap: rb.gap ?? BURST_DEFAULTS.gap,
          every: rb.every ?? BURST_DEFAULTS.every,
          uploadSeconds: cadence.seconds,
        })
      : null;
  // Live sessions in its readings: no burst and no rich reading runs during
  // one.
  const spans = liveSpans(samples, { now: nowMs });
  const expected =
    running && running.perDay > 0
      ? expectedBursts(running.perDay, spans, nowMs - DAY_MS, nowMs)
      : null;
  // Rich captures to expect over the last day: a normal wake's captures on
  // every wake outside live mode, the extra ones on each upload; the wakes
  // since the newest reading are still on the node.
  const perWake = capturesPerWake(rr.mask ?? 0);
  const extras = uploadExtras(rr.mask ?? 0, rr.up ?? 0);
  // From when the rich readings now running began, within the day: none is
  // owed before (the wakes back to the first one's every Nth included). With
  // none at hand, from the settings request that asked for them.
  const desired = settings?.desired || {};
  const asked =
    Number(desired.radar_rich) > 0 || Number(desired.radar_rich_up) > 0
      ? timeOf(settings?.updated_at)
      : null;
  const listedFrom = richLoad.covered ?? nowMs - DAY_MS;
  // Firmware .62's raw uploads: the rich wakes the node held back, from the
  // page's series (every one of its window unless thinned out).
  const heldAll = useMemo(
    () =>
      Array.isArray(held) && heldComplete
        ? held.filter((p) => p?.heldBack && Number.isFinite(p.ms))
        : [],
    [held, heldComplete],
  );
  const began = useMemo(
    () =>
      richStart(samples, [...rich, ...heldAll], {
        every: rr.every ?? 1,
        asked,
        from: listedFrom,
      }),
    [samples, rich, heldAll, rr.every, asked, listedFrom],
  );
  const richFrom = Math.max(
    nowMs - DAY_MS,
    began === null
      ? -Infinity
      : began.source === "first"
        ? began.ms - (rr.every ?? 1) * (wakes.seconds || 0) * 1000
        : began.ms,
  );
  const richSince = richFrom > nowMs - DAY_MS ? began : null;
  const expectedRichDay =
    richOn(rr) && wakes.seconds > 0
      ? expectedRich({
          perWake,
          extras,
          every: rr.every ?? 1,
          wakeSeconds: wakes.seconds,
          uploadSeconds: cadence.seconds,
          spans,
          from: richFrom,
          to: nowMs,
          lastWake: wakes.newest,
        })
      : null;
  const richDay = richTally(summaries, richFrom);
  // Wakes whose captures stayed on the node, so fewer arrive than the wakes
  // would take.
  const heldDay = heldAll.filter((p) => p.ms >= richFrom).length;
  // The day's, beside the counters (which trail what has arrived).
  const richDayAll = richTally(summaries, nowMs - DAY_MS);
  const richAge = newestRich ? nowMs - newestRich.ms : null;
  const richBatch = batchState(richAge, cadence.seconds);
  // The newest burst whose energy the node measured (firmware .57).
  const measured = useMemo(
    () => bursts.bursts.find((b) => b?.energy && typeof b.energy === "object"),
    [bursts.bursts],
  );

  // ---- Status ----
  const lastDay = classic.filter((s) => s.ms >= nowMs - DAY_MS);
  const [chip, tone] =
    richShown && richOn(rr)
      ? [
          rb.mask > 0
            ? "Rich readings on · bursts on"
            : `Rich readings on · ${wakeEveryText(rr.every)}`,
          "is-running",
        ]
      : rb.mask === null && rr.mask === null
        ? ["Not reported yet", "is-off"]
        : rb.mask > 0
          ? [
              `On · every ${rb.every === null || rb.every === 1 ? "" : `${ordinal(rb.every)} `}upload`,
              "is-running",
            ]
          : ["Off", "is-off"];
  const reportedText =
    rb.mask === null
      ? "Not reported yet"
      : rb.mask === 0
        ? "Off"
        : sentence(burstText(rb));
  const richText =
    rr.mask === null ? "Not reported yet" : sentence(richSettingsText(rr));
  const cadenceWords = cadenceText(cadence);

  // ---- The list and the history ----
  // The wakes held back within the list's reach (the series is complete
  // unless thinned out over 7 or 30 days).
  const heldListed = useMemo(
    () => heldAll.filter((p) => p.ms >= (bursts.covered ?? -Infinity)),
    [heldAll, bursts.covered],
  );
  const heldUnlisted =
    !heldComplete && Array.isArray(held) && held.some((p) => p?.heldBack);
  const entries = useMemo(
    () => listEntries(summaries, heldListed),
    [summaries, heldListed],
  );
  const visible = entries.slice(0, shownCount);
  const hours = entries.some((e) => e.kind === "hour" && e.items.length > 1);
  // The hour of a burst opened from elsewhere (the timeline, the history)
  // opens in the list.
  useEffect(() => {
    if (selectedId === null) return;
    const entry = entries.find(
      (e) => e.kind === "hour" && e.items.some((s) => s.id === selectedId),
    );
    if (entry)
      setOpen((old) =>
        old.has(entry.key) ? old : new Set([...old, entry.key]),
      );
    // Only a new selection opens its hour; new bursts arriving do not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);
  const toggle = (key) =>
    setOpen((old) => {
      const next = new Set(old);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const pick = (id) => setSelectedId(id === newest?.id ? null : id);
  const nextWindow = BURST_WINDOWS.find(([m]) => m > range.minutes);
  const chooseWindow = (m) =>
    onMinutes
      ? onMinutes(m)
      : setRange((old) =>
          old.minutes === m
            ? old
            : { minutes: m, from: Math.min(old.from, Date.now() - m * 60000) },
        );
  const historyTo = Math.max(nowMs, newest?.ms ?? -Infinity);
  const historyFrom =
    Math.floor((historyTo - range.minutes * 60000) / 60000) * 60000;
  const inHistory = useMemo(
    () => summaries.filter((s) => s.ms >= historyFrom),
    [summaries, historyFrom],
  );
  const waterfall = useMemo(
    () => burstWaterfall(inHistory, { unit: bursts.unit }),
    [inHistory, bursts.unit],
  );
  // The estimated soil surface (radar/soil/), a dashed line on the history while the viewer has it on.
  const soil = useSoilSurface(mac, 7);
  const soilLines = useMemo(() => {
    const line = soilLine(soil.soil);
    return line ? [line] : undefined;
  }, [soil.soil]);
  // Clipping per burst: the server's clip_pct on its captures; from a server
  // before it, measured here from the stored records of the newest beat
  // signals in the window.
  const clipIds = useMemo(
    () =>
      inHistory
        .flatMap((s) =>
          s.captures.filter(
            (c) =>
              c?.has_adc &&
              c.clip_pct === undefined &&
              c.id !== null &&
              c.id !== undefined,
          ),
        )
        .slice(0, CLIP_DETAILS)
        .map((c) => c.id),
    [inHistory],
  );
  const clipDetails = useRadarCaptureDetails(clipIds, clipCache);
  const points = useMemo(
    () =>
      [...inHistory].reverse().map((s) => {
        const point = burstTrend(s);
        const clip = burstClipping(
          s.captures
            .filter((c) => c?.has_adc)
            .map((c) => {
              if (c.clip_pct !== undefined) return c.clip_pct;
              const adc = clipDetails.get(String(c.id))?.data?.adc;
              return Array.isArray(adc) ? clipping(adc)?.pct : null;
            }),
        );
        return clip === null ? point : { ...point, clip };
      }),
    [inHistory, clipDetails],
  );
  const clipTrend = points.some((p) => p.clip !== undefined);
  // Flicker needs two spectra in a burst: rich readings have one a wake.
  const series = [
    ...BURST_SERIES.filter(
      (s) =>
        s.key !== "flicker" || points.some((p) => Number.isFinite(p.flicker)),
    ),
    ...(clipTrend ? [CLIP_SERIES] : []),
  ];
  const richCapped = richLoad.truncated === "cap";
  const classicCapped = classicLoad.truncated === "cap";
  const loadingOlder = loads.some(
    (l) =>
      l.covered !== null &&
      range.from < l.covered &&
      !l.error &&
      l.truncated !== "cap",
  );

  const loadedSpan =
    windowText(
      Math.round((nowMs - (bursts.covered ?? nowMs - DAY_MS)) / 60000) || 1440,
    ) || "24 h";
  const emptyList =
    richShown && richOn(rr)
      ? `No rich reading in the last ${loadedSpan}. The node takes ${richSettingsText({ mask: rr.mask, up: rr.up, every: rr.every })}, waking ${wakeText(wakes)}, and sends them with its uploads, ${cadenceWords}; ${expectedRichDay?.liveNow ? "rich readings pause while live mode runs, so the next arrive with the first upload after it" : "the next arrive with its next upload"}.${nextWindow ? " Load a longer window to see older ones." : ""}`
      : rb.mask > 0
        ? `No burst in the last ${loadedSpan}. The node bursts ${rb.every === null || rb.every === 1 ? "on every upload" : `on every ${ordinal(rb.every)} upload`}, ${cadenceWords}; ${expected?.liveNow ? "bursts pause while live mode runs, so the next arrives with the first upload after it" : "the next burst arrives with one"}.${nextWindow ? " Load a longer window to see older bursts." : ""}`
        : null;

  // The bursts of the day: in view for a node with classic bursts only, with
  // the counters otherwise.
  const burstDayFact = (!richShown || rb.mask > 0 || lastDay.length > 0) && (
    <Fact label="Bursts, last 24 h">
      {lastDay.length.toLocaleString()}
      {expected ? expectedText(expected) : ""}
    </Fact>
  );
  // The rest of the status, folded: only when there is some.
  const moreFacts =
    modeAllowed ||
    reportedMode !== null ||
    richActive ||
    (richShown && (!!fromReadings || !!countersWords || !!burstDayFact)) ||
    !!measured;
  return (
    <section
      className="field-panel field-stream field-burst"
      aria-labelledby="field-burst-title"
    >
      <div className="field-panel-head">
        <div>
          <h2 id="field-burst-title">Rich readings and bursts</h2>
          <p>
            Raw radar captures from the node's wakes, sent with its uploads,
            with our analysis of each
          </p>
        </div>
        <span className={`field-state ${tone}`} role="status">
          {chip}
        </span>
      </div>
      <div className="field-stream-grid">
        <div className="field-stream-main">
          {missing ? (
            <div className="field-notice">
              This server does not provide bursts yet; the list, the detail and
              the history need the updated backend.
            </div>
          ) : (
            bursts.error && (
              <div className="field-alert" role="alert">
                The bursts could not load: {bursts.error.message}. The view
                keeps what it has and tries again with the page's refresh.
              </div>
            )
          )}
          <div className="field-stream-toolbar">
            <div>
              <h3 className="field-radar-heading">Bursts</h3>
              <p className="field-stream-when">
                {bursts.loaded && bursts.covered !== null
                  ? `${summaries.length.toLocaleString()} since ${sgt(new Date(bursts.covered).toISOString())} · newest first${heldListed.length ? ` · ${heldListed.length.toLocaleString()} more rich wake${heldListed.length === 1 ? "" : "s"} held back on the node` : ""}${hours ? " · rich readings by the hour: open one for its wakes" : ""}${heldUnlisted ? " · the wakes the node held back are listed over 24 h" : ""}`
                  : "—"}
              </p>
            </div>
          </div>
          {!bursts.loaded ? (
            <p className="field-caption">Loading bursts…</p>
          ) : !summaries.length && !heldListed.length ? (
            !missing &&
            !bursts.error && (
              <div className="field-radar-empty">
                {emptyList || (
                  <>
                    No bursts yet. Turn them on in{" "}
                    <Link to="/node-control">
                      Radar rich data on Node Control
                    </Link>
                    : the node takes the first on its next wake that uploads. On
                    cellular a command can take about an hour to reach it.
                  </>
                )}
              </div>
            )
          ) : (
            <>
              <ul
                className="field-burst-list"
                aria-label="Bursts, newest first"
              >
                {visible.map((e) =>
                  e.kind === "hour" && e.items.length > 1 ? (
                    <HourRow
                      key={e.key}
                      entry={e}
                      byId={byId}
                      open={open.has(e.key)}
                      onToggle={() => toggle(e.key)}
                      selectedId={selected?.id ?? null}
                      onSelect={pick}
                      keepRules={keepRules}
                    />
                  ) : e.items[0].held ? (
                    <HeldRow key={e.key} item={e.items[0]} />
                  ) : (
                    <BurstRow
                      key={e.key}
                      summary={e.items[0]}
                      burst={byId.get(e.items[0].id)}
                      selected={selected?.id === e.items[0].id}
                      onSelect={() => pick(e.items[0].id)}
                      keepRules={keepRules}
                    />
                  ),
                )}
              </ul>
              <div className="field-actions" style={{ marginTop: 0 }}>
                {entries.length > shownCount && (
                  <button
                    type="button"
                    className="field-button"
                    onClick={() => setShownCount((n) => n + LIST_PAGE)}
                  >
                    Show {Math.min(LIST_PAGE, entries.length - shownCount)}{" "}
                    older
                  </button>
                )}
                {entries.length <= shownCount && nextWindow && (
                  <button
                    type="button"
                    className="field-button"
                    disabled={loadingOlder}
                    onClick={() => chooseWindow(nextWindow[0])}
                  >
                    Load the last {windowText(nextWindow[0])}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
        <div className="field-stream-side">
          {rb.mask === null && !richShown && (
            <p className="field-caption" style={{ margin: 0 }}>
              The node has not reported radar_burst yet. Firmware rtct.56 or
              later reports the burst settings with its other settings; older
              firmware ignores them.
            </p>
          )}
          <div role="group" aria-label="Burst status">
            <dl className="field-facts field-stream-facts">
              {richShown && <Fact label="Rich readings">{richText}</Fact>}
              <Fact label={richShown ? "Classic bursts" : "The node reports"}>
                {reportedText}
              </Fact>
              {richActive && (
                <Fact label="Wakes">{sentence(wakeText(wakes))}</Fact>
              )}
              <Fact label="Uploads">{sentence(cadenceWords)}</Fact>
              {richActive && (
                <Fact label="Last rich reading">
                  {newestRich ? (
                    <>
                      <Age ms={newestRich.ms} offset={bursts.offset} /> ·{" "}
                      {newestRich.received.toLocaleString()} of{" "}
                      {newestRich.expected ?? "?"} captures
                      {batchText(richBatch, cadence.seconds)}
                    </>
                  ) : bursts.loaded ? (
                    "None loaded"
                  ) : (
                    "—"
                  )}
                </Fact>
              )}
              {(!richShown || rb.mask > 0 || lastBurst) && (
                <Fact label="Last burst">
                  {lastBurst ? (
                    <>
                      <Age ms={lastBurst.ms} offset={bursts.offset} /> ·{" "}
                      {lastBurst.received.toLocaleString()} of{" "}
                      {lastBurst.expected ?? "?"} captures
                    </>
                  ) : bursts.loaded ? (
                    "None loaded"
                  ) : (
                    "—"
                  )}
                  {skipped
                    ? ` · skipped ${sgt(skipped.t)}: ${skipped.text}`
                    : ""}
                </Fact>
              )}
              {!richShown && burstDayFact}
            </dl>
            {moreFacts && (
              <More label="Counters and energy">
                <dl className="field-facts field-stream-facts">
                  {(modeAllowed || reportedMode !== null) && (
                    <Fact label="Raw output mode">
                      {reportedMode !== null
                        ? rawModeText(reportedMode)
                        : "Not reported yet"}
                      {richShown
                        ? " · for classic bursts and streaming; a rich reading takes each part in its own mode"
                        : ""}
                    </Fact>
                  )}
                  {richActive && (
                    <Fact
                      label={
                        richSince
                          ? `Rich readings, since ${dayClockText(richSince.ms)}`
                          : "Rich readings, last 24 h"
                      }
                    >
                      {`${plural(richDay.captures, "capture")} from ${plural(richDay.wakes, "wake")}`}
                      {expectedRichDay
                        ? richExpectedText(expectedRichDay, cadence.seconds)
                        : ""}
                      {heldDay
                        ? ` · ${plural(heldDay, "wake")} held back on the node: ${heldDay === 1 ? "its" : "their"} captures stay there (raw uploads)`
                        : ""}
                    </Fact>
                  )}
                  {richShown && fromReadings && (
                    <Fact label="Captured and failed">
                      {readingCountsText(fromReadings, uncounted)}
                    </Fact>
                  )}
                  {richShown && countersWords && (
                    <Fact label="The node's counters">
                      {countersWords}
                      {shownCounters.source === "reading"
                        ? ` (its reading of ${sgt(shownCounters.t)})`
                        : ` · ${countersNote(shownCounters, { readings: richDayAll.wakes, captures: richDayAll.captures }, { at: countersAt, build })}`}
                    </Fact>
                  )}
                  {richShown && burstDayFact}
                  {measured && (
                    <Fact label="Energy, newest measured">
                      {energyText(measured.energy)}
                    </Fact>
                  )}
                </dl>
              </More>
            )}
          </div>
          <p className="field-caption" style={{ margin: 0 }}>
            What a wake or a burst captures, how many and how often is set in{" "}
            <Link to="/node-control">Radar rich data on Node Control</Link>; its
            battery and data cost is under Before you apply there.
          </p>
        </div>
      </div>
      <div
        className="field-stream-section"
        role="group"
        aria-labelledby="field-burst-detail"
        ref={detailRef}
      >
        <div className="field-stream-toolbar">
          <div>
            <h3 id="field-burst-detail" className="field-radar-heading">
              {selectedId !== null && selected?.id === selectedId
                ? "Inspecting a burst"
                : "Latest burst"}
            </h3>
          </div>
          <div className="field-stream-toolbar-actions">
            {selectedId !== null && (
              <button
                type="button"
                className="field-button"
                onClick={() => setSelectedId(null)}
              >
                Back to the newest
              </button>
            )}
            <Segment
              label="Range"
              options={[
                ["near", "0–2 m"],
                ["full", "Full range"],
              ]}
              value={spectrumRange}
              onChange={setSpectrumRange}
            />
          </div>
        </div>
        {selected && selectedBurst ? (
          <RadarBurstDetail
            key={selected.id}
            mac={mac}
            summary={selected}
            burst={selectedBurst}
            details={details}
            unit={bursts.unit}
            range={spectrumRange}
            keepRules={keepRules}
          />
        ) : (
          <div className="field-radar-empty">
            The newest burst appears here in full, or the one picked in the list
            or the history.
          </div>
        )}
      </div>
      <div
        className="field-stream-section"
        role="group"
        aria-labelledby="field-burst-history"
      >
        <div className="field-stream-toolbar">
          <div>
            <h3 id="field-burst-history" className="field-radar-heading">
              Burst history
            </h3>
            <p className="field-caption" style={{ margin: 0 }}>
              Each burst's mean spectrum over the last{" "}
              {windowText(range.minutes)}; click a column to inspect that burst
              above.
            </p>
          </div>
          <Segment
            label="Window"
            options={BURST_WINDOWS}
            value={range.minutes}
            onChange={chooseWindow}
          />
        </div>
        {loadingOlder && (
          <p className="field-caption">
            Loading the bursts of the last {windowText(range.minutes)}…
          </p>
        )}
        {richCapped && (
          <div className="field-notice">
            This window holds more rich readings than one load takes (one a
            wake), so the list and the history reach back to{" "}
            {richLoad.covered !== null
              ? sgt(new Date(richLoad.covered).toISOString())
              : "part of it"}{" "}
            for them; the classic bursts and the timeline above cover the whole
            window. A shorter window shows all of its rich readings.
          </div>
        )}
        {classicCapped && (
          <div className="field-notice">
            This window holds more bursts than one load takes, so the classic
            bursts reach back to{" "}
            {classicLoad.covered !== null
              ? sgt(new Date(classicLoad.covered).toISOString())
              : "part of it"}{" "}
            only. A shorter window shows all of its bursts.
          </div>
        )}
        {bursts.loaded && !missing && (
          <RadarWaterfall
            data={waterfall}
            minutes={range.minutes}
            range={spectrumRange}
            now={nowMs}
            selectedKey={selectedId !== null ? (selected?.t ?? null) : null}
            onSelect={(column) =>
              setSelectedId(
                column && column.burstId !== newest?.id ? column.burstId : null,
              )
            }
            labels={HISTORY_LABELS}
            lines={soilLines}
          />
        )}
        {bursts.loaded && !missing && <SoilSurfaceControl soil={soil} />}
        {bursts.loaded && waterfall.columns.length > 0 && (
          <>
            <h4 className="field-stream-subhead">
              Trends over the last {windowText(range.minutes)}
            </h4>
            <RadarTrends
              points={points}
              from={historyTo - range.minutes * 60000}
              to={historyTo}
              selectedMs={selectedId !== null ? (selected?.ms ?? null) : null}
              series={series}
              className={
                series.length >= 6
                  ? "is-six"
                  : series.length === 5
                    ? "is-five"
                    : ""
              }
              empty="Trends need at least two bursts in the window."
            />
          </>
        )}
      </div>
    </section>
  );
}

// "rich" or "burst", small, before the time.
function Kind({ kind }) {
  return <span className={`field-burst-kind is-${kind}`}>{kind}</span>;
}

// Our estimate for a burst, in a few words, once analysed.
function oursOf(burst) {
  const analysis = readAnalysis(burst?.analysis);
  const pending = !analysis && analysisPending(burst);
  return {
    analysis,
    pending,
    text:
      analysis && analysis.ourMm !== null
        ? `ours ${mm(analysis.ourMm)}, ${STATES[analysis.state].label.toLowerCase()}`
        : analysis
          ? `ours: ${STATES[analysis.state].label.toLowerCase()}`
          : pending
            ? "ours: analysis pending"
            : null,
  };
}

// One burst in the list: its kind, when, captures received of those
// expected, the raw output mode (what a rich reading holds), the radar's
// picks (and our estimate, once analysed) and a sparkline of the mean
// spectrum.
function BurstRow({ summary: s, burst, selected, onSelect, keepRules = {} }) {
  const path = useMemo(
    () =>
      s.mean
        ? sparkline(s.mean.db, { step: s.mean.step_mm, cal: s.mean.cal_mm })
        : "",
    [s.mean],
  );
  const rich = s.kind === "rich";
  const short = s.expected !== null && s.received < s.expected;
  const skip = burstAccount(burst?.reading?.jjy_burst)?.skip;
  const picks = s.picks.count
    ? mmRange(s.picks.min, s.picks.max)
    : skip && !rich
      ? `skipped: ${skipText(skip)}`
      : "no valid pick";
  const { analysis, text: ours } = oursOf(burst);
  // Firmware .62's raw uploads: why the node sent this wake's raw captures.
  const keep = rich
    ? keepNoteText(burst?.reading?.keep, keepRules, { short: true })
    : null;
  const label = rich
    ? `Rich reading of ${sgt(s.t)}: ${s.received} of ${s.expected ?? "?"} captures (${partsOf(s, true)}), the radar's picks ${picks}`
    : `Burst of ${sgt(s.t)}: ${s.received} of ${s.expected ?? "?"} captures, ${s.mode !== null ? `raw output mode ${s.mode}` : "raw output mode not reported"}, the radar's picks ${picks}`;
  return (
    <li>
      <button
        type="button"
        className={`field-burst-row${selected ? " is-selected" : ""}`}
        aria-pressed={selected}
        aria-label={`${label}${ours ? `, ${ours.replace(/^ours:? /, "our estimate ")}` : ""}${keep ? `, ${keep}` : ""}`}
        onClick={onSelect}
      >
        <span className="field-burst-when">
          <Kind kind={s.kind} />
          {fieldTime(s.t)}
        </span>
        <span className={`field-burst-count${short ? " is-short" : ""}`}>
          {s.received}/{s.expected ?? "?"}
        </span>
        <span className="field-burst-mode">
          {rich ? partsOf(s) : s.mode !== null ? `mode ${s.mode}` : "mode —"}
        </span>
        <span className="field-burst-picks">
          {picks}
          {ours && (
            <span
              className={`field-burst-ours is-${analysis ? analysis.state : "pending"}`}
            >{` · ${ours}`}</span>
          )}
        </span>
        <svg
          className="field-burst-spark"
          viewBox="0 0 96 24"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          {path && <path d={path} />}
        </svg>
        {keep && <span className="field-burst-keep">{keep}</span>}
      </button>
    </li>
  );
}

// A rich wake the node held back (firmware .62's raw uploads): no captures to
// open, only the radar's reading of its wake and our decision from the
// node's own estimate and echo list.
function HeldRow({ item }) {
  const state = STATES[item.state] ? item.state : "unknown";
  const radar = item.radarMm !== null ? mm(item.radarMm) : "no valid pick";
  const ours =
    item.ourMm !== null
      ? `ours ${mm(item.ourMm)}, ${STATES[state].label.toLowerCase()}`
      : `ours: ${STATES[state].label.toLowerCase()}`;
  return (
    <li>
      <div
        className="field-burst-row is-held"
        role="group"
        aria-label={`Rich reading of ${sgt(item.t)}, held back on the node: the radar's reading ${radar}, ${ours.replace(/^ours:? /, "our estimate ")}, decided from the node's echo list; no captures to open`}
      >
        <span className="field-burst-when">
          <Kind kind="rich" />
          {fieldTime(item.t)}
        </span>
        <span className="field-burst-count">—</span>
        <span className="field-burst-mode">no captures</span>
        <span className="field-burst-picks">
          {radar}
          <span className={`field-burst-ours is-${state}`}>{` · ${ours}`}</span>
        </span>
        <span className="field-burst-spark is-empty" aria-hidden="true" />
        <span className="field-burst-keep">
          held back on the node: decided from its echo list
        </span>
      </div>
    </li>
  );
}

// The rich readings of one clock hour (six or seven on a 10-minute wake) as
// one row: the wakes, the captures received of those expected, the radar's
// picks and our estimates across them, and the newest wake's spectrum; it
// opens on its wakes, newest first.
function HourRow({
  entry,
  byId,
  open,
  onToggle,
  selectedId,
  onSelect,
  keepRules = {},
}) {
  const { items } = entry;
  const hour = hourParts(entry.from, entry.to);
  const when = `${hour.day}, ${hour.range}`;
  // The wakes that sent their captures, and those the node held back.
  const sent = items.filter((s) => !s.held);
  const heldBack = items.length - sent.length;
  const received = sent.reduce((n, s) => n + (s.received ?? 0), 0);
  const expected = sent.every((s) => s.expected !== null)
    ? sent.reduce((n, s) => n + s.expected, 0)
    : null;
  const short = expected !== null && received < expected;
  // The radar's picks in the captures sent, and its reading of each wake
  // held back.
  const picked = sent.filter((s) => s.picks.count);
  const radar = [
    ...picked.flatMap((s) => [s.picks.min, s.picks.max]),
    ...items.filter((s) => s.held && s.radarMm !== null).map((s) => s.radarMm),
  ];
  const picks = radar.length
    ? mmRange(Math.min(...radar), Math.max(...radar))
    : "no valid pick";
  const captures = sent.length
    ? `${received} of ${expected ?? "?"} captures`
    : "no captures sent";
  const ours = items.map((s) =>
    s.held
      ? {
          analysis: {
            state: STATES[s.state] ? s.state : "unknown",
            ourMm: s.ourMm,
          },
          pending: false,
        }
      : oursOf(byId.get(s.id)),
  );
  const ready = ours.filter((o) => o.analysis);
  const pending = ours.filter((o) => o.pending).length;
  const tally = ready.reduce(
    (t, o) => ({ ...t, [o.analysis.state]: (t[o.analysis.state] || 0) + 1 }),
    {},
  );
  const states = Object.keys(STATES).filter((k) => tally[k]);
  const values = ready.map((o) => o.analysis.ourMm).filter((v) => v !== null);
  const oursText = ready.length
    ? `ours ${values.length ? mmRange(Math.min(...values), Math.max(...values)) : "no estimate"}, ${
        states.length === 1
          ? STATES[states[0]].label.toLowerCase()
          : states
              .map((k) => `${tally[k]} ${STATES[k].label.toLowerCase()}`)
              .join(", ")
      }${pending ? `, ${pending} pending` : ""}`
    : pending
      ? `ours: ${pending} pending`
      : null;
  const tone =
    states.length === 1 ? states[0] : ready.length ? "mixed" : "pending";
  const spectrum = sent.find((s) => s.mean);
  const path = useMemo(
    () =>
      spectrum
        ? sparkline(spectrum.mean.db, {
            step: spectrum.mean.step_mm,
            cal: spectrum.mean.cal_mm,
          })
        : "",
    [spectrum],
  );
  const id = `field-burst-hour-${entry.from}`;
  const holds = items.some((s) => s.id === selectedId);
  return (
    <li className="field-burst-group">
      <button
        type="button"
        className={`field-burst-row field-burst-hour${holds ? " is-selected" : ""}`}
        aria-expanded={open}
        aria-controls={id}
        aria-label={`Rich readings of ${when}: ${items.length} wakes${heldBack ? ` (${heldBack} held back on the node)` : ""}, ${captures}, the radar's picks ${picks}${oursText ? `, ${oursText.replace(/^ours:? /, "our estimate ")}` : ""}`}
        onClick={onToggle}
      >
        <span className="field-burst-when">
          <ChevronRight
            size={12}
            className={`field-burst-chevron${open ? " is-open" : ""}`}
            aria-hidden="true"
          />
          <Kind kind="rich" />
          {hour.day}, <span className="field-burst-range">{hour.range}</span>
        </span>
        <span className={`field-burst-count${short ? " is-short" : ""}`}>
          {sent.length ? `${received}/${expected ?? "?"}` : "—"}
        </span>
        <span className="field-burst-mode">
          {items.length} wakes{heldBack ? `, ${heldBack} held back` : ""}
        </span>
        <span className="field-burst-picks">
          {picks}
          {oursText && (
            <span className={`field-burst-ours is-${tone}`}>
              {` · ${oursText}`}
            </span>
          )}
        </span>
        <svg
          className="field-burst-spark"
          viewBox="0 0 96 24"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          {path && <path d={path} />}
        </svg>
      </button>
      {open && (
        <ul
          id={id}
          className="field-burst-list field-burst-sub"
          aria-label={`Rich readings of ${when}, newest first`}
        >
          {items.map((s) =>
            s.held ? (
              <HeldRow key={s.id} item={s} />
            ) : (
              <BurstRow
                key={s.id}
                summary={s}
                burst={byId.get(s.id)}
                selected={s.id === selectedId}
                onSelect={() => onSelect(s.id)}
                keepRules={keepRules}
              />
            ),
          )}
        </ul>
      )}
    </li>
  );
}
